"use client";
import { ServiceType } from "@/constants/enum";
import getOrderEstimate from "@/dataLayer/getOrderEstimate";
import saveDefaultAddress from "@/dataLayer/saveDefaultAddress";
import { Merchant } from "@/dto/merchant";
import { Service } from "@/dto/service";
import { lightenColor } from "@/utils/lightenColor";
import { saveToLocalStorage } from "@/utils/localStorageUtils";
import pom from "@/utils/pom";
import { isWithinBusinessHours } from "@/utils/scheduleOrderUtils";
import { saveToSessionStorage } from "@/utils/sessionStorageUtils";
import { LocalStorageKey, SessionStorageKey } from "@/utils/storageKeys";
import { applyDataTestId } from "@/utils/testUtils";
import { DateTime } from "luxon";
import { useTranslations } from "next-intl";
import { useId, useState } from "react";
import DeliveryImageIcon from "../_icons/deliveryImageIcon";
import DeliveryMobileImageIcon from "../_icons/deliveryMobileImageIcon";
import { DeliveryModalError } from "../deliveryModalError/deliveryModalError";
import { EditAddressesModal } from "../editAddressesModal/editAddressesModal";
import { RegularModal } from "../modals/modals";

interface DeliveryOrderModalProps {
  onRequestClose: () => void;
  merchant: Merchant;
  merchantColor: string;
}

export const DeliveryOrderModal = ({
  onRequestClose,
  merchant,
  merchantColor,
}: DeliveryOrderModalProps) => {
  const t = useTranslations("DeliveryOrderModal");
  const [deliveryAddressModalIsOpen, setDeliveryAddressModalIsOpen] =
    useState<boolean>(false);
  const [tempAddress, setTempAddress] = useState("");
  const [deliveryAddressErrorModalOpen, setDeliveryAddressErrorModalOpen] =
    useState<boolean>(false);
  const titleId = useId();
  const subtitleId = useId();

  const merchantService = merchant.services.find(
    (s: Service) =>
      s.type === ServiceType.PICKUP || s.type === ServiceType.WEBSITE
  );
  const nowInMerchantZone = DateTime.now().setZone(merchant.timezone);
  const businessHours = merchant.isRestaurant
    ? merchantService?.hours
    : merchant?.businessHours?.hours;
  const withinBusinessHours = isWithinBusinessHours(
    nowInMerchantZone,
    businessHours,
    merchant?.timezone
  );

  const enabledServices = merchant.services.filter((s: Service) => s.enabled);

  const isOnlyDeliveryEnabled =
    enabledServices.length === 1 && merchant?.isDeliveryEnabled;

  if (!withinBusinessHours || isOnlyDeliveryEnabled) {
    return null;
  }

  return (
    <>
      <RegularModal
        isOpen={!deliveryAddressModalIsOpen && !deliveryAddressErrorModalOpen}
        onRequestClose={onRequestClose}
        overlayClassName="bg-opacity-50"
        contentClassName="bg-white sm:rounded-lg shadow-lg w-full max-w-full h-full sm:h-[580px] sm:w-[70%] mx-auto overflow-hidden"
        focusElementId={titleId}
      >
        <div className="flex flex-col sm:flex-row-reverse h-full">
          <div
            style={{ backgroundColor: lightenColor(merchantColor, 90) }}
            className="items-center justify-center w-full h-[300px] sm:h-full sm:w-1/2 hidden sm:flex"
          >
            <DeliveryImageIcon brandingColor={merchantColor} />
          </div>
          <div className="items-center justify-center sm:bg-green-100 w-full h-[300px] sm:h-full sm:w-1/2 flex sm:hidden">
            <DeliveryMobileImageIcon brandingColor={merchantColor} />
          </div>
          <div className="flex flex-col w-full sm:w-1/2">
            <div className="flex flex-col h-full px-6 sm:px-16">
              <div className="text-center sm:text-left mt-0 sm:mt-14">
                <h2
                  tabIndex={-1}
                  id={titleId}
                  aria-describedby={subtitleId}
                  className="text-3xl sm:text-4xl font-bold outline-0"
                >
                  {t("title")}
                </h2>
                <p
                  id={subtitleId}
                  className="mt-2 text-sm sm:text-base text-gray-700 sm:pt-4"
                >
                  {t("subTitle")}
                </p>
              </div>

              <div className="flex flex-col-reverse sm:flex-col items-center sm:mt-auto sm:mb-16 sm:relative absolute bottom-0 left-0 w-full">
                <button
                  style={{ backgroundColor: merchantColor }}
                  className="px-6 py-4 sm:px-16 text-lg font-semibold text-white sm:rounded-lg w-full sm:w-64"
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    setDeliveryAddressModalIsOpen(true);
                  }}
                >
                  {t("okButton")}
                </button>

                <button
                  onClick={onRequestClose}
                  style={{ color: merchantColor }}
                  className="text-lg font-medium w-full border sm:border-none py-3"
                  data-testid={applyDataTestId(pom.modals.secondaryButton)}
                >
                  {t("cancelButton")}
                </button>
              </div>
            </div>
          </div>
        </div>
      </RegularModal>

      <EditAddressesModal
        isOpen={deliveryAddressModalIsOpen && !deliveryAddressErrorModalOpen}
        onRequestClose={onRequestClose}
        onUpdated={async (address: any, isDefaultDelivery: boolean) => {
          setTempAddress(address);
          try {
            const result = await getOrderEstimate(merchant.id, address);
            if (isDefaultDelivery) {
              await saveDefaultAddress(address);
            }
            saveToSessionStorage(SessionStorageKey.ORDER_ESTIMATES, result);
            saveToLocalStorage(LocalStorageKey.DELIVERY_ADDRESS, address);
            globalThis.dispatchEvent(
              new StorageEvent("storage", {
                key: LocalStorageKey.DELIVERY_ORDER_TYPE,
              })
            );
            onRequestClose();
          } catch {
            setDeliveryAddressErrorModalOpen(true);
          }
        }}
        checkoutCloseHandler={() => {}}
      />

      <DeliveryModalError
        onRequestClose={onRequestClose}
        isOpen={deliveryAddressErrorModalOpen}
        address={tempAddress}
        onEnterNewAddress={() => {
          setDeliveryAddressErrorModalOpen(false);
          setDeliveryAddressModalIsOpen(true);
        }}
        checkoutCloseHandler={() => {}}
      />
    </>
  );
};
